import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  HandWaving,
  FacebookLogo,
  InstagramLogo,
  CircleNotch,
  FloppyDisk,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CHANNELS = [
  {
    key: "messenger",
    label: "Facebook Messenger",
    color: "#1877F2",
    Icon: FacebookLogo,
    placeholder:
      "Hi! 👋 Thanks for reaching out on Messenger. Our team will reply shortly.",
  },
  {
    key: "instagram",
    label: "Instagram Direct",
    color: "#E4405F",
    Icon: InstagramLogo,
    placeholder:
      "Hey! 👋 Thanks for DM-ing us. An agent will get back to you soon.",
  },
];

const MAX_LEN = 1000;

export default function WelcomeMessagesDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState({
    messenger: { enabled: true, text: "" },
    instagram: { enabled: true, text: "" },
  });
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/welcome-messages")
      .then((r) => setCfg(r.data || cfg))
      .catch(() => toast.error("Failed to load welcome messages"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = async (key, partial) => {
    setSavingKey(key);
    try {
      const r = await api.post("/welcome-messages", { [key]: partial });
      setCfg(r.data);
      toast.success("Welcome message saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const onTextChange = (key, val) => {
    setCfg((prev) => ({ ...prev, [key]: { ...prev[key], text: val.slice(0, MAX_LEN) } }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto"
        data-testid="welcome-messages-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 heading">
            <span className="h-8 w-8 grid place-items-center rounded-md bg-[#FFF7ED] text-[#EA580C]">
              <HandWaving size={16} weight="fill" />
            </span>
            Welcome Messages
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Auto-greet first-time customers on Facebook Messenger &amp; Instagram. The
            message is sent only once per contact, the moment their first DM hits the inbox.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div
            className="py-12 grid place-items-center text-[var(--muted)]"
            data-testid="welcome-loading"
          >
            <CircleNotch size={28} className="animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {CHANNELS.map(({ key, label, color, Icon, placeholder }) => {
              const ch = cfg[key] || { enabled: false, text: "" };
              const isSaving = savingKey === key;
              return (
                <div
                  key={key}
                  className="rounded-lg border border-[var(--border)] p-4 space-y-3"
                  data-testid={`welcome-card-${key}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="h-9 w-9 grid place-items-center rounded-md text-white shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      <Icon size={18} weight="fill" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold heading">{label}</div>
                      <div className="text-[11px] text-[var(--muted)]">
                        {ch.enabled ? "Auto-reply ON" : "Auto-reply OFF"}
                      </div>
                    </div>
                    <Switch
                      checked={!!ch.enabled}
                      onCheckedChange={(v) => update(key, { enabled: !!v })}
                      disabled={isSaving}
                      data-testid={`welcome-toggle-${key}`}
                    />
                  </div>

                  <Textarea
                    value={ch.text || ""}
                    onChange={(e) => onTextChange(key, e.target.value)}
                    placeholder={placeholder}
                    rows={4}
                    className="resize-none text-sm font-mono"
                    disabled={!ch.enabled || isSaving}
                    data-testid={`welcome-text-${key}`}
                  />

                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-[var(--muted)]">
                      {(ch.text || "").length}/{MAX_LEN}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => update(key, { text: ch.text || "", enabled: ch.enabled })}
                      disabled={!ch.enabled || isSaving || !(ch.text || "").trim()}
                      className="h-7 gap-1.5"
                      data-testid={`welcome-save-${key}`}
                    >
                      {isSaving ? (
                        <CircleNotch size={12} className="animate-spin" />
                      ) : (
                        <FloppyDisk size={12} weight="fill" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>
              );
            })}

            <div className="text-[11px] text-[var(--muted)] rounded-md border border-dashed border-[var(--border)] p-2.5">
              <strong>Note:</strong> First-time auto-greet only. Returning customers
              won&apos;t receive this message again. Postback events (button clicks)
              are skipped to avoid greeting on intermediate flow steps.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
